<template>
  <div>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 style="margin-bottom: 0;">{{$t('ranu.records.hearing_tests')}}</h4>
        <va-popover
          class="clickable"
          v-if="canAddExam"
          placement="left"
          :message="$t('ranu.exams.add_exam')"
        >
        <span @click.prevent="addExam">
          <i class="glyphicon glyphicon-plus px-3 mr-3"></i>
        </span>
        </va-popover>

      </div>

      <v-client-table
        id="hearingTestsTable"
        ref="hearingTestsTable"
        name="hearingTestsTable"
        :data="record.exams"
        :columns="columns"
        :options="tableOptions"
        >

        <span slot="performed_at" slot-scope="props">
          {{props.row.performed_at.substring(0, 16)}}
        </span>

        <span slot="performed_by" slot-scope="props">
          {{props.row.entity ? $auth.user().entity_id === props.row.entity.id ? props.row.performed_by : props.row.entity.name : props.row.performed_by}}
        </span>

        <span slot="obs" slot-scope="props">
          <span v-if="props.row.obs">
            <span v-if="props.row.obs.length < 60">
              {{props.row.obs}}
            </span>
            <span v-else :title="props.row.obs">
              {{props.row.obs.substring(0, 60)}}
            </span>
          </span>
        </span>

        <span slot="actions" slot-scope="{row}">
          <i v-if="row.exam_files.length >= 1 && row.exam_files[0].mime_type === 'application/pdf'" class="fa fa-file-pdf-o pl-2 pr-2" @click.prevent.stop="handleFile(row)"></i>
          <i v-if="row.exam_files.length >= 1 && row.exam_files[0].mime_type.startsWith('image')" class="fa fa-image pl-2 pr-2" @click.prevent.stop="handleFile(row)"></i>
          <i class="fa fa-pencil pl-2 pr-2" @click.prevent.stop="updateExam(row)"></i>
          <i class="fa fa-times pl-2 pr-2" @click.prevent.stop="deleteExam(row)"></i>
        </span>
      </v-client-table>

    <!-- Create Or Update Exam´s Modal -->
    <CreateOrUpdate
      v-if="isUpdatingExam"
      :record="record"
      :exam="selectedExam"
      :availableExamTypes="availableExamTypes"
      @created="examCreated"
      @updated="examUpdated"
      @modal-closed="modalClosed" />
    <!-- End Create Or Update Exam´s Modal -->

    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      v-if="isDestroyingExam"
      :title="$t('ranu.exams.delete_exam')"
      :body="$t('ranu.exams.delete_modal_body')"
      okClass="btn-danger"
      @modal-confirmed="destroyExam"
      @modal-canceled="cancelDestroyExam" />
    <!-- End Delete Confirmation Modal -->

  </div>
</template>

<script>
import Vue from 'vue'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { ClientTable, Event } from 'vue-tables-2'
import { ExamsService } from '@/api/RanuService'
import { mapState } from 'vuex'
import CreateOrUpdate from '@/components/ranu/exams/CreateOrUpdate'
Vue.use(ClientTable, {}, false, 'bootstrap4')

const examsInfoType = 200
const writeAccessType = 2

export default {
  props: {
    record: {
      type: Object,
      required: true
    }
  },
  components: {
    CreateOrUpdate,
    Event,
    ConfirmationModal
  },
  data: function () {
    return {
      columns: [
        'performed_at',
        'performed_by',
        'process_id',
        'exam_type.name',
        'result_left.name',
        'result_right.name',
        'obs',
        'actions'
      ],
      tableOptions: {
        texts: {
          count: this.$t('tables.records') + ' {from} ' + this.$t('tables.to') + ' {to} ' + this.$t('tables.of') + ' {count}|{count} ' + this.$t('tables.records_') + '|' + this.$t('tables.one_record'),
          first: this.$t('tables.first'),
          last: this.$t('tables.last'),
          filter: this.$t('tables.filter') + ':',
          filterPlaceholder: this.$t('tables.search_text'),
          limit: this.$t('tables.records') + ':',
          page: this.$t('tables.page') + ':',
          noResults: this.$t('tables.no_records'),
          filterBy: this.$t('tables.filter_by') + ' {column}',
          loading: this.$t('tables.loading') + '...',
          defaultOption: this.$t('tables.select') + ' {column}',
        },
        filterable: false,
        sortable: [],
        headings: {
          performed_at: this.$t('ranu.records.performed_at'),
          performed_by: this.$t('ranu.records.performed_by'),
          process_id: this.$t('ranu.records.process_id'),
          'result_left.name': this.$t('ranu.records.result_left'),
          'result_right.name': this.$t('ranu.records.result_right'),
          'status.name': this.$t('ranu.records.status'),
          'exam_type.name': this.$t('ranu.records.exam_type'),
          'actions': this.$t('ranu.exams.actions'),
          obs: this.$t('ranu.obs')
        },
        columnsClasses: {
          result_left: 'result_left',
          result_right: 'result_right'
        },
        skin: 'table table-striped table-hover is-fullwidth',
        sortIcon: {
          is: 'fa-sort',
          base: 'fa fa-fw',
          up: 'fa-sort-up',
          down: 'fa-sort-down'
        },
        columnsDropdown: false,
        pagination: {
          chunk: 4,
          nav: 'scroll'
        }
      },
      isUpdatingExam: false,
      selectedExam: null,
      isDestroyingExam: false,
      selectedExamToDestroy: null,
    }
  },
  methods: {
    examCreated: function (payload) {
      this.$emit('exam-created', payload)
      setTimeout(() => {
        this.isUpdatingExam = false
      }, 500)
    },
    examUpdated: function (payload) {
      this.$emit('exam-updated', payload)
      setTimeout(() => {
        this.isUpdatingExam = false
      }, 500)
    },
    addExam: function () {
      this.isUpdatingExam = true
    },
    deleteExam: function (exam) {
      this.selectedExamToDestroy = exam
      this.isDestroyingExam = true
    },
    updateExam: function (exam) {
      this.isUpdatingExam = true
      this.selectedExam = JSON.parse(JSON.stringify(exam))
    },
    handleFile: function (exam) {
      this.$http({
        url: `${this.$store.state.ranu_server}/records/exams/files/${exam.exam_files[0].id}`,
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: exam.exam_files[0].mime_type }))
          window.open(url, '_blank')
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    modalClosed: function () {
      this.isUpdatingExam = false
      this.selectedExam = null
    },
    destroyExam: function () {
      ExamsService.destroy(this.selectedExamToDestroy.record.id, this.selectedExamToDestroy.id)
        .then(response => {
          this.showToast(this.$t('ranu.records.record_deleted'), this.$store.state.successToastOptions)
          this.$emit('exam-deleted', { exam: this.selectedExamToDestroy })
        })
        .catch(errors => {
          this.showToast(this.$t('ranu.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.cancelDestroyExam()
        })
    },
    cancelDestroyExam: function () {
      setTimeout(() => {
        this.isDestroyingExam = false
      }, 500)
    }
  },
  computed: {
    ...mapState({
      examTypes: (state) => state.ranu.examTypesOptions,
      statuses: (state) => state.ranu.statusesOptions
    }),
    permissions: function () {
      let ranuService = this.$store.state.services.find(service => service.url === this.$store.state.ranu_server) || null
      if (ranuService) {
        return ranuService.user_permissions
      }
      return null
    },
    writePermissions: function () {
      return this.permissions.filter(permission => permission.info_type === examsInfoType && permission.access_type >= writeAccessType)
    },
    availableExamTypes: function () {
      return this.examTypes.filter(examType => !this.record.exams.some(exam => exam.exam_type.id === examType.id))
    },
    canAddExam: function () {
      return this.writePermissions.some(permission => permission.entity_id === this.record.entity.id || permission.entity_id === null) && this.availableExamTypes.length
    }
  }
}
</script>

<style lang="scss">

th.result_left, td.result_left {
  text-align: center;
}

th.result_right, td.result_right {
  text-align: center;
}
</style>
